import { lookup } from 'fp-ts/lib/Array';
import { Eq, eqString } from 'fp-ts/lib/Eq';
import { fromNullable, isNone, none, Option, some } from 'fp-ts/lib/Option';
import { identity } from 'fp-ts/lib/function';
import { Component, createElement, ReactNode } from 'react';
import { isSelectOpen } from '../../app';
import { makeIcon } from '../../components/button';
import tr from '../../locale';
import { tryNumber, Undefined } from '../../util';
import {
    BUTTON,
    DETAILS,
    DIV,
    INPUT,
    LABEL,
    NodeOrOptional,
    OPTION,
    SELECT,
    SPAN,
    SUMMARY,
} from '../elements';

const renderFilterSelectItem =
    <T>(renderItem: (a: T) => ReactNode, select: (a: T) => void) =>
    (v: T) =>
        BUTTON(
            {
                className: 'tail-item',
                onClick: () => {
                    select(v);
                },
            },
            SPAN('item__control'),
            DIV('item__label', renderItem(v)),
        );

// type State = [string, boolean];
// let stateList: State[] = [];

// const toggle = (id: string, newStatus: boolean) => {
//     stateList = stateList
//         .filter(([i, _]) => i !== id)
//         .map<State>(([i, _]) => [i, false])
//         .concat([[id, newStatus]]);
//     __forceRefreshState()
// };

// export const renderSelect = <T>(
//     S: Eq<T>,
//     renderItem: (a: T) => ReactNode,
//     select: (a: T) => void,
//     key = uniqId(),
// ) => {
//     stateList.push([key, false]);

//     const mkItem = renderSelectItem(renderItem, select);

//     return (list: T[] | Readonly<T[]>, selected: Option<T>) => {

//         const isOpen = fromNullable(stateList.find(([i, status]) => i === key && status === true));
//         const renderTail = isOpen.map(() => {
//             const tail = list
//                 .filter(i => selected.fold(true, s => S.equals(i, s) !== true))
//                 .map(mkItem);
//             return DIV({ className: 'tail' }, ...tail)
//         })

//         return DIV(
//             {
//                 className: 'select__wrapper active',
//                 onClick: () => toggle(key, isOpen.isNone())
//             },
//             DIV(
//                 {
//                     className: 'select'
//                     // FIXME this is a temporary hack, we should be VDOMy
//                 },
//                 renderSelected(renderItem, selected),
//                 renderTail,
//             )
//         );
//     }
// };

const renderSelectItem =
    <T>(toString: (a: T) => string, isSelected: boolean) =>
    (object: T, i: number) =>
        OPTION(
            {
                className: 'interactive',
                value: i,
                selected: isSelected,
                key: i,
            },
            toString(object),
        );

const selectPlaceholder = (placeholder: Undefined<string>) =>
    fromNullable(placeholder).map(placeholder =>
        OPTION({ disabled: true, selected: true, value: '' }, placeholder),
    );

export interface SelectOpts {
    /** text rendered in the `<label>` above the dropdown */
    label?: string;
    /** disabled prompt option shown while nothing is selected */
    placeholder?: string;
}

/**
 * Renders a native `<select>` dropdown.
 *
 * Curried: configure it once, then call the returned function with the list of
 * options and the currently selected one on each render. Options are keyed by
 * their position in `list`; the matching option is flagged via `eq`, and
 * `select` is called with the picked item on change.
 *
 * @param name css class on the wrapper and `name`/`id` of the `<select>`
 * @param toString label shown for each option
 * @param select called with the chosen item when the selection changes
 * @param eq Eq instance used to flag the matching option as selected
 * @param opts optional `label` and `placeholder` (see {@link SelectOpts})
 * @returns a function taking the option list and the selected option (or `none`)
 */
export const makeSelect =
    <T>(
        name: string,
        toString: (a: T) => string,
        select: (a: T) => void,
        eq: Eq<T>,
        opts: SelectOpts = {},
    ) =>
    (list: T[] | Readonly<T[]>, selected: Option<T>) => {
        const items = [...list].map<NodeOrOptional>((e, i) =>
            renderSelectItem(
                toString,
                selected.fold(false, s => eq.equals(s, e)),
            )(e, i),
        );

        return DIV(
            `select ${name}`,
            LABEL('', opts.label),
            SELECT(
                {
                    name,
                    id: name,
                    key: name,
                    onChange: ev =>
                        tryNumber(ev.currentTarget.value)
                            .chain(i => lookup(i, [...list]))
                            .map(select),
                },
                isNone(selected) ? selectPlaceholder(opts.placeholder) : none,
                ...items,
            ),
        );
    };

/**
 * Having two optional arguments is a brittle interface. Prefer using makeSelect.
 * Left here for backward compatibility.
 * @deprecated use makeSelect
 */
export const renderSelect =
    <T>(
        name: string,
        toString: (a: T) => string,
        select: (a: T) => void,
        eq: Eq<T>,
        label?: string,
        placeholder?: string,
    ) =>
    (list: T[] | Readonly<T[]>, selected: Option<T>) =>
        makeSelect(name, toString, select, eq, { label, placeholder })(
            list,
            selected,
        );

/**
 * This component functionality was intergrated in renderSelect.
 * Left here for backward compatibility.
 * @deprecated use makeSelect
 */
export const renderSelectWithDefaultStr =
    <T>(
        name: string,
        toString: (a: T) => string,
        select: (a: T) => void,
        eq: Eq<T>,
        defaultStr: string,
        label?: string,
    ) =>
    (list: T[] | Readonly<T[]>, selected: Option<T>) =>
        renderSelect(
            name,
            toString,
            select,
            eq,
            label,
            defaultStr,
        )(list, selected);

interface FilterState {
    input: string;
    active: boolean;
}

/** Props of {@link renderSelectFilter} / the underlying `SelectFilter` component. */
export interface FilterProps<T> {
    list: T[] | Readonly<T[]>;
    renderItem: (a: T) => ReactNode;
    select: (a: T) => void;
    selected: Option<T>;
    S: Eq<T>;
    toString: (a: T) => string;
    title: () => string;
}

class SelectFilter<T> extends Component<FilterProps<T>, FilterState> {
    constructor(props: FilterProps<T>) {
        super(props);
    }

    render() {
        const { renderItem, select, list, selected, S, toString, title } =
            this.props;
        const inputValue = this.state?.input ?? '';
        const active = this.state?.active ?? false;
        const pat = inputValue.toLocaleLowerCase();
        const mkItem = renderFilterSelectItem(renderItem, select);
        const tail = list
            .filter(
                i =>
                    toString(i).toLocaleLowerCase().includes(pat) &&
                    selected.fold(true, s => S.equals(i, s) !== true),
            )
            .map(mkItem);

        const input = INPUT({
            type: 'text',
            placeholder: tr.core('filter'),
            onChange: el =>
                this.setState({ active, input: el.currentTarget.value }),
        });

        return DETAILS(
            `select-filter__wrapper`,
            SUMMARY(
                '',
                selected.map(renderItem).getOrElse(title()),
                // DIV('btn btn-3 icon-only', nameToString('angle-down'))
            ),
            DIV('select-tail__wrapper', input, DIV('tail', ...tail)),
        );
    }
}

/**
 * renders a dropdown list with a filter input
 *
 * @param S Eq instance to compare the entries.
 * @param renderItem renders each item in the dropdown list
 * @param select called when an item is clicked
 * @param toString returns a strings to represent the item. The string will be used to filter the list
 * @param title the placeholder on the list component
 * @returns a function taking a list of items and some selected item or none.
 */
export const renderSelectFilter =
    <T>(
        S: Eq<T>,
        renderItem: (a: T) => ReactNode,
        select: (a: T) => void,
        toString: (a: T) => string,
        title = () => '',
    ) =>
    (list: T[] | Readonly<T[]>, selected: Option<T>) => {
        const props: FilterProps<T> = {
            S,
            renderItem,
            select,
            toString,
            list,
            selected,
            title,
        };
        return createElement(SelectFilter, props);
    };

// We also need a select with a filter, where we also can add an élément if not in the list:

/** Props of {@link SelectFilterAdd}: {@link FilterProps} plus the create `action` and an open-state `key`. */
export interface FilterOrActionProps<T> {
    list: T[] | Readonly<T[]>;
    renderItem: (a: T) => ReactNode;
    select: (a: T) => void;
    selected: Option<T>;
    S: Eq<T>;
    toString: (a: T) => string;
    action: (input: string) => void;
    key: string;
    title: () => string;
}

/**
 * Filterable dropdown (specialised to `string` items) that can also create a
 * new entry: the filter box is paired with an add button which calls `action`
 * with the current input. Use {@link renderSelectFilterAdd} to instantiate it.
 */
export class SelectFilterAdd extends Component<
    FilterOrActionProps<string>,
    FilterState
> {
    constructor(props: FilterOrActionProps<string>) {
        super(props);
    }

    render() {
        const {
            renderItem,
            select,
            list,
            selected,
            S,
            toString,
            action,
            key,
            title,
        } = this.props;
        const inputValue = this.state?.input ?? '';
        const active = this.state?.active ?? false;

        const pat = inputValue.toLocaleLowerCase();
        const mkItem = renderFilterSelectItem(renderItem, select);
        const tail = list
            .filter(
                i =>
                    toString(i).toLocaleLowerCase().includes(pat) &&
                    selected.fold(true, s => S.equals(i, s) !== true),
            )
            .map(mkItem);

        const input = INPUT({
            type: 'text',
            placeholder: tr.core('filter'),
            onChange: el =>
                this.setState({ active, input: el.currentTarget.value }),
        });

        const addButton = makeIcon('add', 3, 'plus', {
            text: () => tr.core('add'),
            position: 'top-left',
        });

        return DETAILS(
            {
                className: `select-filter__wrapper`,
                open: isSelectOpen(key),
            },
            SUMMARY(
                '',
                selected
                    .chain(s => (s === '' ? none : some(renderItem(s))))
                    .getOrElse(title()),
                // DIV('btn btn-3 icon-only', nameToString('angle-down'))
            ),
            DIV(
                'select-tail__wrapper',
                SPAN(
                    'input__wrapper',
                    input,
                    addButton(() => action(inputValue)),
                ),
                DIV('tail', ...tail),
            ),
        );
    }
}

/**
 * Instantiates a {@link SelectFilterAdd}: a string dropdown with a filter input
 * and an add button. Items are compared with `eqString` and filtered by their
 * own value.
 *
 * @param renderItem renders each item in the dropdown list
 * @param select called when an existing item is clicked
 * @param action called with the current filter input when the add button is pressed
 * @param title placeholder shown on the closed dropdown
 * @returns a function taking the list, the selected item (or `none`) and a
 *   unique `key` driving the open/close state
 */
export const renderSelectFilterAdd =
    (
        renderItem: (a: string) => ReactNode,
        select: (a: string) => void,
        action: (input: string) => void,
        title = () => '',
    ) =>
    (
        list: string[] | Readonly<string[]>,
        selected: Option<string>,
        key: string,
    ) => {
        const props: FilterOrActionProps<string> = {
            S: eqString,
            renderItem,
            select,
            toString: identity,
            list,
            selected,
            action,
            key,
            title,
        };
        return createElement(SelectFilterAdd, props);
    };
